/** «Шаблон xlsx» / «Загрузить xlsx»: файл проверяется бэкендом целиком — либо всё, либо ничего;
 *  при 422 — таблица ошибок построчно. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Upload } from "lucide-react";
import React, { useRef } from "react";
import { toast } from "sonner";

import { downloadTemplate, importPricesXlsx } from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { saveBlob } from "@/lib/download";
import { apiErrorText, xlsxErrors } from "./errors";
import { invalidatePrices } from "./queries";
import { ErrorBox, Modal } from "./ui";

export function XlsxButtons(): React.ReactElement {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const tpl = useMutation({
    mutationFn: () => downloadTemplate(),
    onSuccess: (blob) => saveBlob(blob, "purchase-prices-template.xlsx"),
    onError: (e) => toast.error(`Шаблон не скачался: ${apiErrorText(e)}`),
  });
  const imp = useMutation({
    mutationFn: (file: File) => importPricesXlsx(file),
    onSuccess: async (r) => {
      toast.success(`Из файла добавлено цен: ${r.written}`);
      await invalidatePrices(qc);
    },
  });
  const rowErrors = imp.isError ? xlsxErrors(imp.error) : null;

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => tpl.mutate()} disabled={tpl.isPending}>
        <Download /> Шаблон xlsx
      </Button>
      <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={imp.isPending}>
        <Upload /> {imp.isPending ? "Загружаю…" : "Загрузить xlsx"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        data-testid="xlsx-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) imp.mutate(file);
        }}
      />
      {imp.isError && (
        <Modal title="Файл не принят" onClose={() => imp.reset()} wide>
          {rowErrors ? (
            <div className="space-y-2">
              <p className="text-sm">{rowErrors.message}</p>
              <div className="max-h-[60vh] overflow-auto rounded-md border">
                <table className="w-full text-sm" data-testid="xlsx-errors">
                  <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5 font-medium">Строка</th>
                      <th className="px-2 py-1.5 font-medium">Столбец</th>
                      <th className="px-2 py-1.5 font-medium">Ошибка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowErrors.errors.map((er, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 tabular-nums">{er.row}</td>
                        <td className="px-2 py-1">{er.column ?? "—"}</td>
                        <td className="px-2 py-1">{er.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <ErrorBox error={imp.error} what="Загрузка не удалась" />
          )}
        </Modal>
      )}
    </>
  );
}
